import {useQuery, useQueryClient} from '@tanstack/react-query'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {Alert, AlertDescription} from '@/components/ui/alert'
import {Loader2} from 'lucide-react'
import {useToast} from '@/hooks/use-toast'
import {useEffect, useRef} from 'react'
import {routes} from '@/lib/routes'
import {JobExecutionStatus} from '@/types/background-jobs'
import type {AttributeEntityType} from '@/types/attribute-entity-type'

interface MigrationJobBannerProps {
    definitionName: string
    jobId: string
    entityType: AttributeEntityType
    onCompleted?: () => void
}

const TERMINAL_STATUSES: string[] = [
    JobExecutionStatus.COMPLETED,
    JobExecutionStatus.COMPLETED_WITH_ERRORS,
    JobExecutionStatus.FAILED,
    JobExecutionStatus.CANCELLED,
]

const POLL_INTERVAL_MS = 3000

export function MigrationJobBanner({
    definitionName, jobId, entityType, onCompleted}: MigrationJobBannerProps) {
    const services = useServices()
    const i18n = useI18n()
    const {toast} = useToast()
    const queryClient = useQueryClient()
    const toastShownRef = useRef(false)

    const {data: execution} = useQuery({
         
        queryKey: ['job-execution', jobId],
        queryFn: () => services.jobs.executionDetail(jobId),
        refetchInterval: (query) => {
            const status = query.state.data?.status
            if (status && TERMINAL_STATUSES.includes(status)) return false
            return POLL_INTERVAL_MS
        },
        enabled: !!jobId,
    })

    const status = execution?.status
    const isTerminal = status ? TERMINAL_STATUSES.includes(status) : false

    useEffect(() => {
        if (isTerminal && !toastShownRef.current) {
            toastShownRef.current = true
             
            queryClient.invalidateQueries({queryKey: ['attribute-definitions', entityType]})
            onCompleted?.()

            if (status === JobExecutionStatus.COMPLETED) {
                toast({description: i18n.attributeDefinitions.updateSuccess})
            } else if (status === JobExecutionStatus.COMPLETED_WITH_ERRORS) {
                toast({
                    description: i18n.attributeDefinitions.migration.completedWithErrors,
                    variant: 'destructive',
                })
            } else if (status === JobExecutionStatus.FAILED) {
                toast({
                    description: i18n.attributeDefinitions.migration.failed,
                    variant: 'destructive',
                })
            }
        }
    }, [isTerminal, status, toast, i18n, queryClient, entityType, onCompleted])

    if (isTerminal && status === JobExecutionStatus.COMPLETED) {
        return null
    }

    const jobDashboardUrl = routes.backgroundJobs.executions.detail(jobId)

    if (status === JobExecutionStatus.COMPLETED_WITH_ERRORS) {
        return (
            <Alert variant="default" data-testid={`migration-job-banner-${definitionName}`} className="border-amber-500 bg-status-warning-subtle dark:bg-status-warning-subtle/20">
                <AlertDescription className="flex items-center gap-2">
                    <span>{i18n.attributeDefinitions.migration.completedWithErrors}</span>
                    <a href={jobDashboardUrl} className="underline text-sm" data-testid={`migration-job-link-${definitionName}`}>
                        {i18n.attributeDefinitions.migration.viewJobDashboard}
                    </a>
                </AlertDescription>
            </Alert>
        )
    }

    if (status === JobExecutionStatus.FAILED || status === JobExecutionStatus.CANCELLED) {
        return (
            <Alert variant="destructive" data-testid={`migration-job-banner-${definitionName}`}>
                <AlertDescription className="flex items-center gap-2">
                    <span>{i18n.attributeDefinitions.migration.failed}</span>
                    <a href={jobDashboardUrl} className="underline text-sm" data-testid={`migration-job-link-${definitionName}`}>
                        {i18n.attributeDefinitions.migration.viewJobDashboard}
                    </a>
                </AlertDescription>
            </Alert>
        )
    }

    return (
        <Alert data-testid={`migration-job-banner-${definitionName}`} className="border-blue-500 bg-status-info-subtle dark:bg-status-info-subtle/20">
            <AlertDescription className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>{i18n.attributeDefinitions.migration.inProgress}</span>
            </AlertDescription>
        </Alert>
    )
}
